import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LayoutComponent } from './layout/components/layout.component';
import { AuthGuard } from './guards/auth.guard';
import { PermissionGuard } from './guards/permission.guard';
import { LoyaltyAccessGuard } from './guards/loyalty-access.guard';

const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'dashboard',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'dashboard' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/dashboard/dashboard.module').then(
            (m) => m.DashboardModule
          ),
      },
    ],
  },
  {
    path: 'ai-insights',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'ai-insights' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/ai-insights/ai-insights.module').then(
            (m) => m.AIInsightsModule
          ),
      },
    ],
  },
  {
    path: 'setting',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'policies' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/setting/setting.module').then(
            (m) => m.SettingModule
          ),
      },
    ],
  },
  {
    path: 'categories',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'categories' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/categories/categories.module').then(
            (m) => m.CategoriesModule
          ),
      },
    ],
  },
  // Old URL kept as a redirect for bookmarks; the permission key is still 'business-subject'.
  { path: 'business-subject', redirectTo: 'business-objective', pathMatch: 'full' },
  { path: 'business-subject/add', redirectTo: 'business-objective/add', pathMatch: 'full' },
  {
    path: 'business-objective',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'business-subject' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/business-subject/business-subject.module').then(
            (m) => m.BusinessSubjectModule
          ),
      },
    ],
  },
  {
    path: 'sectors',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'sectors' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/sectors/sectors.module').then(
            (m) => m.SectorsModule
          ),
      },
    ],
  },
  {
    path: 'referral-config',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'referral-config' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/referral-config/referral-config.module').then(
            (m) => m.ReferralConfigModule
          ),
      },
    ],
  },
  {
    path: 'loyalty',
    component: LayoutComponent,
    canActivate: [AuthGuard, LoyaltyAccessGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/loyalty/loyalty.module').then(
            (m) => m.LoyaltyModule
          ),
      },
    ],
  },
  {
    path: 'interests',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    data: { pageKey: 'interests' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/interest/interest.module').then(
            (m) => m.InterestModule
          ),
      },
    ],
  },
  {
    path: 'activity-logs',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    data: { pageKey: 'activity-logs' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/activity-logs/activity-logs.module').then(
            (m) => m.ActivityLogsModule
          ),
      },
    ],
  },
  {
    path: 'super-admin-logs',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    data: { pageKey: 'super-admin-logs' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/super-admin-logs/super-admin-logs.module').then(
            (m) => m.SuperAdminLogsModule
          ),
      },
    ],
  },
  {
    path: 'quiz',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'quiz' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/quiz/quiz.module').then(
            (m) => m.QuizModule
          ),
      },
    ],
  },
  {
    path: 'contests',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'contest' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/Contests/contests.module').then(
            (m) => m.ContestsModule
          ),
      },
    ],
  },
  {
    path: 'users',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'users' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/users/user.module').then(
            (m) => m.UserModule
          ),
      },
    ],
  },
  {
    path: 'clients',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'client' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/clients/client.module').then(
            (m) => m.ClientModule
          ),
      },
    ],
  },
  {
    path: 'client-qr',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'client' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/client-qr/client-qr.module').then(
            (m) => m.ClientQrModule
          ),
      },
    ],
  },
  {
    path: 'branding',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'client' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/branding/branding.module').then(
            (m) => m.BrandingModule
          ),
      },
    ],
  },
  {
    path: 'analysis-reports',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'analysis-reports' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/analysis-reports/analysis-reports.module').then(
            (m) => m.AnalysisReportsModule
          ),
      },
    ],
  },
  {
    path: 'subscription',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'subscription' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/subscription/subscription.module').then(
            (m) => m.SubscriptionModule
          ),
      },
    ],
  },
  {
    path: 'feedback',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'feedback' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/feedback/feedback.module').then(
            (m) => m.FeedbackModule
          ),
      },
    ],
  },
  {
    path: 'notification',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'notification' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/Notifications/notifications.module').then(
            (m) => m.NotificationsModule
          ),
      },
    ],
  },
  {
    path: 'terms-condition',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'terms-condition' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/terms-condition/terms-condition.module').then(
            (m) => m.TermsConditionModule
          ),
      },
    ],
  },
  {
    path: 'ads',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'ads' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/ads/ads.module').then(
            (m) => m.AdsModule
          ),
      },
    ],
  },
  {
    path: 'request',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'request' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/request/request.module').then(
            (m) => m.RequestModule
          ),
      },
    ],
  },
  {
    path: 'newsletter',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'newsletter' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/newsletter/newsletter.module').then(
            (m) => m.NewsletterModule
          ),
      },
    ],
  },
  {
    path: 'email-templates',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'email-templates' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/email-templates/email-templates.module').then(
            (m) => m.EmailTemplatesModule
          ),
      },
    ],
  },
  {
    path: 'task-management',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'task-management' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/task-management/task-management.module').then(
            (m) => m.TaskManagementModule
          ),
      },
    ],
  },
  {
    path: 'profile',
    component: LayoutComponent,
    canActivate: [AuthGuard, PermissionGuard],
    data: { pageKey: 'profile' },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/profile/profile.module').then(
            (m) => m.ProfileModule
          ),
      },
    ],
  },
  // {
  //   path: 'plan',
  //   component: LayoutComponent,
  //   canActivate: [AuthGuard, PermissionGuard],
  //   children: [
  //     {
  //       path: '',
  //       loadChildren: () =>
  //         import('./features/plan/plan.module').then(
  //           (m) => m.PlanModule
  //         ),
  //     },
  //   ],
  // },
  // TEMPORARY: Debug permissions page — remove when done
  {
    path: 'debug-permissions',
    loadComponent: () =>
      import('./features/debug-permissions/debug-permissions.component').then(
        (m) => m.DebugPermissionsComponent
      ),
  },
  { path: '', redirectTo: 'auth/login', pathMatch: 'full' },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./shared/maintenance/maintenance.component').then(
        (m) => m.MaintenanceComponent
      ),
  },
  // Where a module the account isn't allowed to use sends the user
  // (?module=Loyalty names it) — see LoyaltyAccessGuard / LoyaltyAccessService.
  {
    path: 'access-restricted',
    loadComponent: () =>
      import('./shared/access-restricted/access-restricted.component').then(
        (m) => m.AccessRestrictedComponent
      ),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./shared/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
